import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Save as SaveIcon, Add as AddIcon, Delete as DeleteIcon, Restore as RestoreIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import ConfirmDialog from '../../ConfirmDialog';

export default function SettingsTab() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // State for settings form
  const [settings, setSettings] = useState({
    customer_read_query: '',
    customer_read_query_columns: '',
    prospect_read_query: '',
    prospect_read_query_columns: '',
    contact_read_query: '',
    contact_read_query_columns: '',
    create_endpoint: '',
    read_endpoint: '',
    // Default values
    default_values: {
      salesman1: '',
      terms_code: '',
      Active: '',
      print_ackn_flag: '',
      contact_type: ''
    },
    customer_id_prefix: '',
    // Tax configuration
    tax_configuration: {
      default_tax_code: '',
      default_taxable_flag: '',
      state_tax_rules: []
    },
    // Add state for country code mapping and validation error
    country_code_mapping: {
      mappings: ''
    }
  });

  // State for reset confirmation dialog
  const [showResetDialog, setShowResetDialog] = useState(false);

  // Add state for country code mapping and validation error
  const [mappingValidationError, setMappingValidationError] = useState('');

  // Add to settings state if not present
  useEffect(() => {
    if (!('country_code_mapping' in settings)) {
      setSettings(prev => ({ ...prev, country_code_mapping: { mappings: '' } }));
    }
  }, [settings]);

  // Fetch settings
  const {
    data: settingsData,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['customerSyncSettings', 'shopify-to-erp'],
    queryFn: async () => {
      const response = await api.getShopifyToErpCustomerSyncSettings();
      return response.data.data;
    },
    staleTime: 0
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (updatedSettings) => {
      const response = await api.updateShopifyToErpCustomerSyncSettings(updatedSettings);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Customer sync settings updated successfully');
      queryClient.invalidateQueries({ queryKey: ['customerSyncSettings', 'shopify-to-erp'] });
    },
    onError: (error) => {
      toast.error(`Failed to update settings: ${error.response?.data?.message || error.message}`);
    }
  });

  // Reset settings mutation
  const resetSettingsMutation = useMutation({
    mutationFn: async () => {
      const response = await api.resetShopifyToErpCustomerSyncSettings();
      return response.data;
    },
    onSuccess: () => {
      toast.success('Shopify to ERP customer sync settings reset to default successfully');
      queryClient.invalidateQueries({ queryKey: ['customerSyncSettings', 'shopify-to-erp'] });
      setShowResetDialog(false);
    },
    onError: (error) => {
      toast.error(`Failed to reset settings: ${error.response?.data?.message || error.message}`);
      setShowResetDialog(false);
    }
  });

  // Initialize form state when data is loaded
  useEffect(() => {
    if (settingsData?.customerSyncSettings) {
      const customerSyncSettings = settingsData.customerSyncSettings;

      const processedSettings = {
        customer_read_query: customerSyncSettings.customer_read_query || '',
        customer_read_query_columns: customerSyncSettings.customer_read_query_columns || '',
        prospect_read_query: customerSyncSettings.prospect_read_query || '',
        prospect_read_query_columns: customerSyncSettings.prospect_read_query_columns || '',
        contact_read_query: customerSyncSettings.contact_read_query || '',
        contact_read_query_columns: customerSyncSettings.contact_read_query_columns || '',
        create_endpoint: customerSyncSettings.create_endpoint || '',
        read_endpoint: customerSyncSettings.read_endpoint || '',
        customer_id_prefix: customerSyncSettings.customer_id_prefix || '',
        default_values: {
          salesman1: customerSyncSettings.default_values?.salesman1 || customerSyncSettings.salesman1 || '',
          terms_code: customerSyncSettings.default_values?.terms_code || customerSyncSettings.terms_code || '',
          Active: customerSyncSettings.default_values?.Active || customerSyncSettings.Active || '',
          print_ackn_flag: customerSyncSettings.default_values?.print_ackn_flag || customerSyncSettings.print_ackn_flag || '',
          contact_type: customerSyncSettings.default_values?.contact_type || customerSyncSettings.contact_type || ''
        },
        tax_configuration: {
          default_tax_code: customerSyncSettings.tax_configuration?.default_tax_code || '',
          default_taxable_flag: customerSyncSettings.tax_configuration?.default_taxable_flag || '',
          state_tax_rules: customerSyncSettings.tax_configuration?.state_tax_rules || []
        },
        country_code_mapping: {
          mappings: customerSyncSettings.country_code_mapping?.mappings || ''
        }
      };

      setSettings(processedSettings);
    }
  }, [settingsData]);

  // Handle form input changes
  const handleInputChange = (event) => {
    const { name, value } = event.target;

    // Check if this is a default value field
    if (['salesman1', 'terms_code', 'Active', 'print_ackn_flag', 'contact_type'].includes(name)) {
      setSettings(prev => ({
        ...prev,
        default_values: {
          ...prev.default_values,
          [name]: value
        }
      }));
    } else if (['default_tax_code', 'default_taxable_flag'].includes(name)) {
      // Handle tax configuration default fields
      setSettings(prev => ({
        ...prev,
        tax_configuration: {
          ...prev.tax_configuration,
          [name]: value
        }
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle state tax rule changes
  const handleStateTaxRuleChange = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      tax_configuration: {
        ...prev.tax_configuration,
        state_tax_rules: prev.tax_configuration.state_tax_rules.map((rule, i) =>
          i === index ? { ...rule, [field]: value } : rule
        )
      }
    }));
  };

  // Add new state tax rule
  const addStateTaxRule = () => {
    setSettings(prev => ({
      ...prev,
      tax_configuration: {
        ...prev.tax_configuration,
        state_tax_rules: [...prev.tax_configuration.state_tax_rules, { state_code: '', tax_code: '', taxable_flag: '' }]
      }
    }));
  };

  // Remove state tax rule
  const removeStateTaxRule = (index) => {
    setSettings(prev => ({
      ...prev,
      tax_configuration: {
        ...prev.tax_configuration,
        state_tax_rules: prev.tax_configuration.state_tax_rules.filter((_, i) => i !== index)
      }
    }));
  };

  // Validation function for 2-digit to 3-digit mapping
  const validateCountryCodeMappings = (mappingsText) => {
    // Handle both string and object inputs
    let mappingsString;
    if (typeof mappingsText === 'object' && mappingsText !== null) {
      mappingsString = JSON.stringify(mappingsText);
    } else if (typeof mappingsText === 'string') {
      mappingsString = mappingsText;
    } else if (!mappingsText) {
      return { isValid: true, error: '' };
    } else {
      return { isValid: false, error: 'Invalid mappings format' };
    }

    if (!mappingsString || mappingsString.trim() === '' || mappingsString.trim() === '{}') {
      return { isValid: true, error: '' };
    }

    try {
      const mappings = JSON.parse(mappingsString);
      if (typeof mappings !== 'object' || mappings === null) {
        return { isValid: false, error: 'Mappings must be a JSON object' };
      }
      for (const [key, value] of Object.entries(mappings)) {
        if (typeof key !== 'string' || typeof value !== 'string') {
          return { isValid: false, error: 'All keys and values must be strings' };
        }
        if (key.length !== 2) {
          return { isValid: false, error: 'All keys must be 2-digit ISO country codes' };
        }
        if (value.length !== 3) {
          return { isValid: false, error: 'All values must be 3-digit ERP country codes' };
        }
      }
      return { isValid: true, error: '' };
    } catch (error) {
      return { isValid: false, error: 'Invalid JSON format' };
    }
  };

  // Handle form submission
  const handleSubmit = (event) => {
    event.preventDefault();
    const validation = validateCountryCodeMappings(settings.country_code_mapping.mappings);
    if (!validation.isValid) {
      toast.error(`Country code mappings validation failed: ${validation.error}`);
      return;
    }

    // Format columns string as array
    let columnsArray = settings.customer_read_query_columns;
    if (typeof columnsArray === 'string' && columnsArray.trim() !== '') {
      columnsArray = columnsArray.split(',').map(col => col.trim());
    }

    // Parse mappings from string to object if not empty
    let parsedMappings = settings.country_code_mapping.mappings;
    if (typeof parsedMappings === 'string' && parsedMappings.trim() !== '') {
      try {
        parsedMappings = JSON.parse(parsedMappings);
      } catch (e) {
        toast.error('Invalid JSON in country code mappings');
        return;
      }
    }

    const updatedSettings = {
      ...settings,
      customer_read_query_columns: columnsArray,
      prospect_read_query_columns: settings.prospect_read_query_columns.trim(),
      contact_read_query_columns: settings.contact_read_query_columns.trim(),
      country_code_mapping: {
        ...settings.country_code_mapping,
        mappings: parsedMappings
      }
    };

    updateSettingsMutation.mutate(updatedSettings);
  };

  // Handle reset settings
  const handleResetSettings = () => {
    setShowResetDialog(true);
  };

  const handleConfirmReset = () => {
    resetSettingsMutation.mutate();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity='error' sx={{ mb: 3 }}>
        Error loading settings: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: '15px' }}>
        <Box display='flex' justifyContent='space-between' alignItems='center' mb={4}>
          <Box display='flex' alignItems='center'>
            <SettingsIcon color='primary' sx={{ mr: 1 }} />
            <Typography variant='h6'>
              Shopify to ERP Customer Sync Settings
            </Typography>
          </Box>
          {isAdmin ? (
            <>
              <Button
                variant='contained'
                color='warning'
                onClick={handleResetSettings}
                disabled={!isAdmin || resetSettingsMutation.isPending}
                startIcon={<RestoreIcon />}
              >
                Reset
              </Button>
            </>
          ) : (<></>)}
        </Box>
        {!settingsData?.customerSyncSettings ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>

              <Grid item xs={12}>
                <Typography variant='h6' color='primary' gutterBottom>
                API Endpoints
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Create Endpoint'
                  name='create_endpoint'
                  value={settings.create_endpoint}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  variant='outlined'
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Read Endpoint'
                  name='read_endpoint'
                  value={settings.read_endpoint}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  variant='outlined'
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant='h6' color='primary' gutterBottom>
                Customer Settings
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='Customer Read Query'
                  name='customer_read_query'
                  value={settings.customer_read_query}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Used to read customer from ERP'
                  variant='outlined'
                  sx={{ mb: 2 }}
                  multiline
                  rows={3}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='Customer Read Query Columns'
                  name='customer_read_query_columns'
                  value={settings.customer_read_query_columns}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Comma-separated list of columns to fetch from ERP.'
                  variant='outlined'
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant='h6' color='primary' gutterBottom>
                Prospect Settings
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='Prospect Read Query'
                  name='prospect_read_query'
                  value={settings.prospect_read_query}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Used to read prospect from ERP'
                  variant='outlined'
                  sx={{ mb: 2 }}
                  multiline
                  rows={3}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='Prospect Read Query Columns'
                  name='prospect_read_query_columns'
                  value={settings.prospect_read_query_columns}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Comma-separated list of columns to fetch from ERP.'
                  variant='outlined'
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant='h6' color='primary' gutterBottom>
                Contact Settings
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='Contact Read Query'
                  name='contact_read_query'
                  value={settings.contact_read_query}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Used to read contact from ERP'
                  variant='outlined'
                  sx={{ mb: 2 }}
                  multiline
                  rows={3}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='Contact Read Query Columns'
                  name='contact_read_query_columns'
                  value={settings.contact_read_query_columns}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Comma-separated list of columns to fetch from ERP.'
                  variant='outlined'
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant='h6' color='primary' gutterBottom>
                Default Values
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label='Customer ID Prefix'
                  name='customer_id_prefix'
                  value={settings.customer_id_prefix}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='The prefix to use for customer creation in ERP'
                  variant='outlined'
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label='Salesman1'
                  name='salesman1'
                  value={settings.default_values.salesman1}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='The default value for the salesman1 field'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label='Terms Code'
                  name='terms_code'
                  value={settings.default_values.terms_code}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='The default value for the terms_code field'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label='Active'
                  name='Active'
                  value={settings.default_values.Active}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='The default value for the Active field'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label='Print Acknowledgement Flag'
                  name='print_ackn_flag'
                  value={settings.default_values.print_ackn_flag}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='The default value for the print_ackn_flag field'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label='Contact Type'
                  name='contact_type'
                  value={settings.default_values.contact_type}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='The default value for the contact_type field'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant='h6' color='primary' gutterBottom>
                Tax Configuration
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Default Tax Code'
                  name='default_tax_code'
                  value={settings.tax_configuration.default_tax_code}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default tax code for customers with no state-specific rule'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Default Taxable Flag'
                  name='default_taxable_flag'
                  value={settings.tax_configuration.default_taxable_flag}
                  onChange={handleInputChange}
                  disabled={!isAdmin}
                  helperText='Default taxable flag for customers with no state-specific rule'
                  variant='outlined'
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant='subtitle1' color='primary'>
                  State-Specific Tax Rules
                  </Typography>
                  {isAdmin ? (
                    <>
                      <Button
                        variant='outlined'
                        size='small'
                        startIcon={<AddIcon />}
                        onClick={addStateTaxRule}
                        disabled={!isAdmin}
                      >
                      Add Rule
                      </Button>
                    </>
                  ) : (<></>)}
                </Box>

                <TableContainer component={Paper} variant='outlined'>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>State Code</TableCell>
                        <TableCell>Tax Code</TableCell>
                        <TableCell>Taxable Flag</TableCell>
                        {isAdmin ? (<TableCell align='center'>Actions</TableCell>) : (<></>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {settings.tax_configuration.state_tax_rules.map((rule, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <TextField
                              size='small'
                              value={rule.state_code}
                              onChange={(e) => handleStateTaxRuleChange(index, 'state_code', e.target.value)}
                              placeholder='e.g., VA'
                              fullWidth
                              disabled={!isAdmin}
                              sx={{
                                mb: { xs: '0px !important' },
                                my: { xs: '20px !important' },
                                width: { xs: '100px !important', sm: '100% !important'}
                              }}
                              required
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size='small'
                              value={rule.tax_code}
                              onChange={(e) => handleStateTaxRuleChange(index, 'tax_code', e.target.value)}
                              placeholder='e.g., 047'
                              fullWidth
                              disabled={!isAdmin}
                              sx={{
                                mb: { xs: '0px !important' },
                                my: { xs: '20px !important' },
                                width: { xs: '100px !important', sm: '100% !important'}
                              }}
                              required
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size='small'
                              value={rule.taxable_flag}
                              onChange={(e) => handleStateTaxRuleChange(index, 'taxable_flag', e.target.value)}
                              placeholder='e.g., A'
                              fullWidth
                              disabled={!isAdmin}
                              sx={{
                                mb: { xs: '0px !important' },
                                my: { xs: '20px !important' },
                                width: { xs: '100px !important', sm: '100% !important'}
                              }}
                              required
                            />
                          </TableCell>
                          {isAdmin ? (<TableCell align='center'>
                            <IconButton
                              size='small'
                              color='error'
                              onClick={() => removeStateTaxRule(index)}
                              disabled={!isAdmin}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>) : (<></>)}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              <Grid item xs={12}>
                <Typography variant='subtitle2' color='text.secondary' sx={{ mt: 2, mb: 1 }}>
                Country Code Mappings (2-digit to 3-digit)
                </Typography>
                <Typography variant='caption' color='text.secondary' sx={{ mb: 2, display: 'block' }}>
                Enter the country code mappings in JSON format. Example: {'{"AW": "ABW", "AF": "AFG"}'}
                </Typography>
                <TextField
                  fullWidth
                  label='Country Code Mappings'
                  name='country_code_mapping.mappings'
                  value={typeof settings.country_code_mapping?.mappings === 'string' ? settings.country_code_mapping.mappings : JSON.stringify(settings.country_code_mapping?.mappings || '', null, 2)}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSettings(prev => ({
                      ...prev,
                      country_code_mapping: {
                        ...prev.country_code_mapping,
                        mappings: value
                      }
                    }));
                    const validation = validateCountryCodeMappings(value);
                    setMappingValidationError(validation.error);
                  }}
                  disabled={!isAdmin}
                  helperText={mappingValidationError || 'Enter country code mappings in JSON format'}
                  error={!!mappingValidationError}
                  variant='outlined'
                  sx={{ mb: 2 }}
                  multiline
                  rows={20}
                  InputProps={{
                    style: { fontFamily: 'monospace', fontSize: '12px' }
                  }}
                  required
                />
              </Grid>

            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              {isAdmin ? (
                <>
                  <Button
                    type='submit'
                    variant='contained'
                    color='primary'
                    startIcon={updateSettingsMutation.isPending ? <CircularProgress size={20} color='inherit' /> : <SaveIcon />}
                    disabled={!isAdmin || updateSettingsMutation.isPending || !!mappingValidationError}
                  >
                    {updateSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
                  </Button>
                </>
              ) : (<></>)}
            </Box>
          </form>
        )}
      </Paper>

      <ConfirmDialog
        open={showResetDialog}
        onClose={() => setShowResetDialog(false)}
        onCancel={() => setShowResetDialog(false)}
        onConfirm={handleConfirmReset}
        confirmColor='warning'
        title='Reset Settings to Default'
        message='Are you sure you want to reset all Shopify to ERP customer sync settings to their default values? This action cannot be undone.'
        confirmText='Reset'
        cancelText='Cancel'
        severity='warning'
        isLoading={resetSettingsMutation.isPending}
      />
    </Box>
  );
}
